import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { getProvider, getTokenBalance } from '../utils/web3';
import { CHAIN_CONFIGS, TOKEN_INFO } from '../utils/constants';
import { ChainConfig, TokenType } from '../types';

export interface ChainBalance {
  chainId: string;
  chainName: string;
  chainIcon: string;
  nativeBalance: string;
  nativeSymbol: string;
  nativeIcon: string; // Added native currency icon
  tokenBalances: {
    [key in TokenType]?: {
      balance: string;
      symbol: string;
      icon: string;
    };
  };
  isLoading: boolean;
  error: string | null;
}

export const usePortfolio = (walletAddress: string) => {
  const [balances, setBalances] = useState<ChainBalance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBalances = async () => {
      if (!walletAddress) {
        setBalances([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Get all chain configs
        let chainConfigs = Object.values(CHAIN_CONFIGS);
        
        // Define the order of chains
        const chainOrder = [
          'shido',
          'ethereum',
          'binance', 'bsc', 'bnb',
          'avalanche', 'avax',
          'polygon', 'matic',
          'arbitrum', 'arb',
          'base'
        ];
        
        // Sort chains according to the specified order
        chainConfigs = chainConfigs.sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          
          // Find the index of each chain in the order array
          // We check if the chain name includes any of the keywords
          const aIndex = chainOrder.findIndex(keyword => aName.includes(keyword));
          const bIndex = chainOrder.findIndex(keyword => bName.includes(keyword));
          
          // If both chains are found in the order array, sort by their position
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          
          // If only one chain is found, prioritize it
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          
          // If neither chain is found, maintain original order
          return 0;
        });
        
        const initialBalances: ChainBalance[] = chainConfigs.map(config => ({
          chainId: config.chainId,
          chainName: config.name,
          chainIcon: config.icon || '',
          nativeBalance: '0',
          nativeSymbol: config.nativeCurrency?.symbol || 'ETH',
          nativeIcon: config.icon || '', // Use chain icon for native currency
          tokenBalances: {},
          isLoading: true,
          error: null
        }));

        setBalances(initialBalances);

        // Fetch balances for each chain in parallel
        const balancePromises = chainConfigs.map(async (config, index) => {
          try {
            // Create a direct provider for this specific chain
            const provider = new ethers.providers.JsonRpcProvider(config.rpcUrl);
            if (!provider) {
              throw new Error(`Failed to initialize provider for ${config.name}`);
            }

            // Get native token balance
            let nativeBalance = '0';
            try {
              const balanceWei = await provider.getBalance(walletAddress);
              nativeBalance = ethers.utils.formatEther(balanceWei);
            } catch (nativeError) {
              console.error(`Error fetching native balance for ${config.name}:`, nativeError);
            }

            // Get token balances
            const tokenBalances: ChainBalance['tokenBalances'] = {};
            const tokenTypes: TokenType[] = ['USDC', 'USDT'];

            for (const tokenType of tokenTypes) {
              const tokenAddress = config[tokenType];
              if (tokenAddress && tokenAddress !== '') {
                try {
                  // Create a contract instance directly
                  const tokenContract = new ethers.Contract(tokenAddress, [
                    'function balanceOf(address) view returns (uint256)',
                    'function decimals() view returns (uint8)'
                  ], provider);
                  
                  // Get token balance and decimals
                  const balance = await tokenContract.balanceOf(walletAddress);
                  let decimals = 18; // Default
                  
                  try {
                    decimals = await tokenContract.decimals();
                  } catch (decimalsError) {
                    console.warn(`Could not get decimals for ${tokenType} on ${config.name}, using default:`, decimalsError);
                  }
                  
                  const formattedBalance = ethers.utils.formatUnits(balance, decimals);
                  
                  tokenBalances[tokenType] = {
                    balance: formattedBalance,
                    symbol: tokenType,
                    icon: TOKEN_INFO[tokenType].icon
                  };
                } catch (tokenError) {
                  console.error(`Error fetching ${tokenType} balance on ${config.name}:`, tokenError);
                  // Still add the token with zero balance for display
                  tokenBalances[tokenType] = {
                    balance: '0',
                    symbol: tokenType,
                    icon: TOKEN_INFO[tokenType].icon
                  };
                }
              }
            }

            return {
              ...initialBalances[index],
              nativeBalance,
              tokenBalances,
              isLoading: false,
              error: null
            };
          } catch (chainError: any) {
            console.error(`Error fetching balances for ${config.name}:`, chainError);
            return {
              ...initialBalances[index],
              isLoading: false,
              error: chainError.message || `Failed to fetch balances for ${config.name}`
            };
          }
        });

        const results = await Promise.all(balancePromises);
        setBalances(results);
        setIsLoading(false);
      } catch (err: any) {
        console.error('Error fetching portfolio balances:', err);
        setError(err.message || 'Failed to fetch portfolio balances');
        setIsLoading(false);
      }
    };

    fetchBalances();
  }, [walletAddress]);

  // Refresh balances manually
  const refreshBalances = () => {
    setIsLoading(true);
    // This will trigger the useEffect to run again
    setBalances([]);
  };

  return {
    balances,
    isLoading,
    error,
    refreshBalances
  };
};
